import { useState } from 'react';
import ChatMessage from './ChatMessage';

const PAGE_SIZE = 10;

function TrashIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

export default function HistorySection({ messages, onTogglePersistent, onSavePrompt, onClearHistory }) {
  const [visibleCount, setVisibleCount] = useState(0); // 0 = fully collapsed
  const [confirmClear, setConfirmClear] = useState(false);

  if (!messages || messages.length === 0) return null;

  const total = messages.length;
  const hiddenCount = total - visibleCount;
  const visibleMessages = messages.slice(total - visibleCount);

  const showMore = () =>
    setVisibleCount(c => Math.min(c + PAGE_SIZE, total));

  const showAll = () => setVisibleCount(total);
  const collapse = () => setVisibleCount(0);

  const handleClearClick = () => setConfirmClear(true);
  const handleClearCancel = () => setConfirmClear(false);
  const handleClearConfirm = () => {
    setConfirmClear(false);
    setVisibleCount(0);
    onClearHistory?.();
  };

  return (
    <div className="history-section">

      {/* Collapsed header — always visible */}
      <div className="history-header">
        <div className="history-header-line" />
        <div className="history-header-center">
          <span className="history-icon">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="12 8 12 12 14 14" />
              <path d="M3.05 11a9 9 0 1 1 .5 4" />
            </svg>
          </span>
          <span className="history-label">
            {visibleCount === 0
              ? `${total} saved message${total !== 1 ? 's' : ''} from long-term memory`
              : `Showing ${visibleCount} of ${total} saved messages`}
          </span>

          <div className="history-controls">
            {visibleCount < total && (
              <>
                <button className="history-btn" onClick={showMore}>
                  + {Math.min(PAGE_SIZE, hiddenCount)} earlier
                </button>
                {hiddenCount > PAGE_SIZE && (
                  <button className="history-btn" onClick={showAll}>
                    Show all
                  </button>
                )}
              </>
            )}
            {visibleCount > 0 && (
              <button className="history-btn history-btn--collapse" onClick={collapse}>
                Collapse
              </button>
            )}

            {/* Separator dot */}
            {onClearHistory && (
              <>
                <span className="history-controls-sep">·</span>
                {confirmClear ? (
                  <span className="history-clear-confirm">
                    <span className="history-clear-confirm-label">Clear all saved history?</span>
                    <button className="history-clear-confirm-yes" onClick={handleClearConfirm}>
                      Yes, clear
                    </button>
                    <button className="history-clear-confirm-no" onClick={handleClearCancel}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    className="history-btn history-btn--clear"
                    onClick={handleClearClick}
                    title="Clear all saved history for this topic"
                  >
                    <TrashIcon />
                    Clear
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="history-header-line" />
      </div>

      {/* Visible history messages */}
      {visibleCount > 0 && (
        <div className="history-messages">
          {visibleMessages.map((msg, i) => (
            <ChatMessage
              key={`history-${total - visibleCount + i}`}
              message={msg}
              index={messages.indexOf(msg)}
              onTogglePersistent={onTogglePersistent}
              onSavePrompt={onSavePrompt}
              isHistory
            />
          ))}
        </div>
      )}

      {/* Divider between history and current session */}
      {visibleCount > 0 && (
        <div className="history-session-divider">
          <div className="history-header-line" />
          <span className="history-divider-label">Current session</span>
          <div className="history-header-line" />
        </div>
      )}
    </div>
  );
}
